import React, { useMemo } from 'react';
import { OrganizationDataReport } from '../../models/general/notifications/report-data-set';
import { getAggregatedData } from '../../utils/organization-stats-handlers';
import PageWithHeaderFooter from './Page';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import WeeklyCleaningPerformanceChart from '../organizations/stats/WeeklyCleaningPerformanceChart';
import WeeklyCleaningDurationChart from '../organizations/stats/WeeklyCleaningDurationChart';
import WeeklyCleanedAreaChart from '../organizations/stats/WeeklyCleanedAreaChart';
import { DateTime } from 'luxon';
import RobotCards from '../organizations/stats/RobotCards';

const OrganizationStatsPages: React.FC<{
  name: string;
  pages: number;
  pageIndex: number;
  data: OrganizationDataReport;
  start: DateTime;
  end: DateTime;
}> = ({ name, pageIndex, pages, data, start, end }) => {
  const { t } = useTranslation();

  const aggregatedData = useMemo(
    () => getAggregatedData(data.deploymentWeeks),
    [data.deploymentWeeks],
  );

  const filterWeeks = (week: string) => {
    return (
      DateTime.fromISO(week).toMillis() > start.toMillis() &&
      DateTime.fromISO(week).toMillis() < end.toMillis()
    );
  };

  const robotCount = useMemo(
    () => Array.from(new Set(data.deploymentWeeks.flatMap((dw) => dw.robotId))).length,
    [data.deploymentWeeks],
  );
  const locationCount = useMemo(
    () => Array.from(new Set(data.deploymentWeeks?.flatMap((dw) => dw.organizationId))).length,
    [data.deploymentWeeks],
  );

  return (
    <React.Fragment>
      <PageWithHeaderFooter
        header={`${t('notification-trigger-DASHBOARD_SUMMARY')} - ${name}`}
        footer={t('info-sphere-footnote')}
        pages={`${pageIndex + 2} / ${pages}`}
      >
        <Stack spacing={8}>
          <Stack spacing={2}>
            <RobotCards
              robotCount={robotCount}
              cleanedArea={aggregatedData?.totalCleanedArea ?? 0}
              operatingTime={aggregatedData?.totalOperatingTime ?? 0}
              locationCount={locationCount}
              fixGrid
            />
          </Stack>
          <WeeklyCleaningPerformanceChart
            aggregatedData={aggregatedData}
            filterWeeks={filterWeeks}
          />
        </Stack>
      </PageWithHeaderFooter>

      <PageWithHeaderFooter
        header={`${t('notification-trigger-DASHBOARD_SUMMARY')} - ${name}`}
        footer={t('info-sphere-footnote')}
        pages={`${pageIndex + 3} / ${pages}`}
      >
        <Stack spacing={8}>
          <WeeklyCleanedAreaChart aggregatedData={aggregatedData} filterWeeks={filterWeeks} />
          <WeeklyCleaningDurationChart aggregatedData={aggregatedData} filterWeeks={filterWeeks} />
        </Stack>
      </PageWithHeaderFooter>
    </React.Fragment>
  );
};

export default OrganizationStatsPages;
